
import { Box, Button, CircularProgress, Grid, Paper } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StyledButton from "../../components/reusable/StyledButton";
import { Form } from 'react-final-form';
import FormControlledTextField from "../../components/reusable/FormControlledTextField";
import { ILoginFormData, IRegisterFormData, IRegisterFormWithConfirm, IResetPasswordFormData } from "../../constants/interfaces";
import { IApiErrorResponse } from "../../constants/api-interfaces";
import { formatPhoneNumber, normalizePhoneNumber, shouldPreventPhonePrefixDeletion } from "../../constants/utils";
import { loginValidation, registerValidation, resetPasswordValidation } from "../../constants/validationSchemas";
import { forgotPassword, login, register, resendVerification, resetPassword, verifyEmail } from "../../constants/api";
import { useAuth } from "../../contexts/AuthContext";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useLocation, useNavigate } from "react-router";
import { ChangeEvent, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";

const useStyles = makeStyles()((theme) => ({
  loginWrapper: {
    justifyItems: 'center',
    padding: '2em',
    width: '100%',
    maxWidth: 480,
  },
}))


export default function Login() {
  const { classes } = useStyles();
  const { auth, authDispatch } = useAuth();
  const { snackbarDispatch } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const [isAuthLoading, setAuthLoading] = useState<boolean>(false);
  const [isRegisterMode, setIsRegisterMode] = useState<boolean>(false);
  const [hasRegisteredSuccessfully, setHasRegisteredSuccessfully] = useState<boolean>(false);
  const [hasRequestedPasswordReset, setHasRequestedPasswordReset] = useState<boolean>(false);
  const [verificationStatusMessage, setVerificationStatusMessage] = useState<string>('');
  const [verificationStatusError, setVerificationStatusError] = useState<boolean>(false);
  const lastVerifiedTokenRef = useRef<string>('');

  const resetToken = useMemo(() => new URLSearchParams(location.search).get('reset')?.trim() || '', [location.search]);
  const verifyToken = useMemo(() => new URLSearchParams(location.search).get('verify')?.trim() || '', [location.search]);
  const isVerifyMode = Boolean(verifyToken);
  const isResetMode = !isVerifyMode && Boolean(resetToken);

  useEffect(() => {
    if (auth?.success) {
      navigate('/dashboard')
    }
  }, [auth])

  useEffect(() => {
    if (!isVerifyMode || !verifyToken) {
      return;
    }

    if (lastVerifiedTokenRef.current === verifyToken) {
      return;
    }

    lastVerifiedTokenRef.current = verifyToken;

    const verifyAccount = async () => {
      try {
        setAuthLoading(true);
        const response = await verifyEmail(verifyToken);
        const successMessage =
          response.data.message || 'Email verified successfully. Please wait for your account to be enabled.';
        setVerificationStatusError(false);
        setVerificationStatusMessage(successMessage);
        snackbarDispatch({
          type: 'setSnackbar',
          payload: {
            message: successMessage,
            error: false,
          },
        });
      } catch (error) {
        const axiosError = error as AxiosError<IApiErrorResponse>;
        const errorMessage = axiosError.response?.data?.message || 'Email verification failed.';
        setVerificationStatusError(true);
        setVerificationStatusMessage(errorMessage);
        snackbarDispatch({
          type: 'setSnackbar',
          payload: {
            message: errorMessage,
            error: true,
          },
        });
      } finally {
        setAuthLoading(false);
      }
    };

    verifyAccount();
  }, [isVerifyMode, verifyToken, snackbarDispatch]);

  const handleLogin = async(values: ILoginFormData) => {
    try {
      setAuthLoading(true)
      const res = await login(values)
      authDispatch({ type: 'setAuth', payload: res.data })
      snackbarDispatch({ type: 'setSnackbar', payload: { message: 'Login successful', error: false }})
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({ type: 'setSnackbar', payload: { message: axiosError.response?.data?.message || 'Login failed', error: true }})
    } finally {
      setAuthLoading(false)
    }
  }

  const handleRegister = async(values: IRegisterFormWithConfirm) => {
    try {
      setAuthLoading(true)
      const { confirmPassword, ...registerPayload } = values;
      await register(registerPayload)
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Registration successful. Check your email to verify your account and complete sign up.',
          error: false,
        },
      })
      setHasRegisteredSuccessfully(true)
      setIsRegisterMode(false)
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({ type: 'setSnackbar', payload: { message: axiosError.response?.data?.message || 'Registration failed', error: true }})
    } finally {
      setAuthLoading(false)
    }
  }

  const handleForgotPassword = async (email?: string) => {
    const normalizedEmail = String(email || '').trim();

    if (!normalizedEmail) {
      snackbarDispatch({ type: 'setSnackbar', payload: { message: 'Email is a required field', error: true }});
      return;
    }

    try {
      setAuthLoading(true)
      await forgotPassword(normalizedEmail)
      setHasRequestedPasswordReset(true)
      setHasRegisteredSuccessfully(false)
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Password reset email sent successfully.',
          error: false,
        },
      })
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({ type: 'setSnackbar', payload: { message: axiosError.response?.data?.message || 'Password reset request failed', error: true }})
    } finally {
      setAuthLoading(false)
    }
  }

  const handleResetPassword = async (values: IResetPasswordFormData) => {
    try {
      setAuthLoading(true)
      await resetPassword(resetToken, values.password)
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: 'Password updated successfully. Please log in with your new password.',
          error: false,
        },
      })
      navigate('/login', { replace: true })
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({ type: 'setSnackbar', payload: { message: axiosError.response?.data?.message || 'Password reset failed', error: true }})
    } finally {
      setAuthLoading(false)
    }
  }

  const handleResendVerification = async (email?: string) => {
    const normalizedEmail = String(email || '').trim();

    if (!normalizedEmail) {
      snackbarDispatch({ type: 'setSnackbar', payload: { message: 'Email is a required field', error: true }});
      return;
    }

    try {
      setAuthLoading(true)
      const response = await resendVerification(normalizedEmail)
      setIsRegisterMode(false)
      setHasRegisteredSuccessfully(true)
      setHasRequestedPasswordReset(false)
      snackbarDispatch({
        type: 'setSnackbar',
        payload: {
          message: response.data.message || 'Registration successful. Check your email to verify your account and complete sign up.',
          error: false,
        },
      })
    } catch (error) {
      const axiosError = error as AxiosError<IApiErrorResponse>;
      snackbarDispatch({ type: 'setSnackbar', payload: { message: axiosError.response?.data?.message || 'Unable to resend verification email', error: true }})
    } finally {
      setAuthLoading(false)
    }
  }

  const initialValues = isResetMode
    ? { password: '', confirmPassword: '' }
    : isRegisterMode
      ? { email: '', password: '', confirmPassword: '', firstName: '', lastName: '', dateOfBirth: '', phoneNumber: '+1-' }
      : { email: '', password: '' };

  return (
    <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 5, minHeight: '80vh'}}>
      <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center'}}>
        <Paper className={classes.loginWrapper} elevation={3}>
          {isVerifyMode ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {isAuthLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                  <CircularProgress />
                </Box>
              ) : null}
              {verificationStatusMessage ? (
                <Box
                  sx={{
                    width: '100%',
                    px: 2,
                    py: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: verificationStatusError ? 'rgba(254, 202, 202, 0.35)' : 'rgba(96, 165, 250, 0.2)',
                    border: verificationStatusError ? '1px solid rgba(239, 68, 68, 0.55)' : '1px solid rgba(96, 165, 250, 0.5)',
                    color: verificationStatusError ? 'error.dark' : 'primary.dark',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                  }}
                >
                  {verificationStatusMessage}
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <StyledButton
                  variant="outlined"
                  type="button"
                  onClick={() => navigate('/login', { replace: true })}
                >
                  Back to login
                </StyledButton>
              </Box>
            </Box>
          ) : (
          <Form
            onSubmit={isResetMode ? handleResetPassword : (isRegisterMode ? handleRegister : handleLogin)}
            initialValues={initialValues}
            validate={isResetMode ? resetPasswordValidation : (isRegisterMode ? registerValidation : loginValidation)}
            render={({ handleSubmit, form }) => (
              <form onSubmit={handleSubmit} noValidate>
                <Grid container spacing={3}>
                  {isRegisterMode && !isResetMode && (
                    <>
                      <Grid size={{xs: 12}} >
                        <FormControlledTextField variant="outlined"
                          name="firstName"
                          size="small"
                          fullWidth={true}
                          label="First Name"
                          required={true}
                        />
                      </Grid>
                      <Grid size={{xs: 12}} >
                        <FormControlledTextField variant="outlined"
                          name="lastName"
                          size="small"
                          fullWidth={true}
                          label="Last Name"
                          required={true}
                        />
                      </Grid>
                    </>
                  )}
                  {!isResetMode && (
                    <>
                      <Grid size={{xs: 12 }} >
                        <FormControlledTextField variant="outlined"
                          name="email"
                          size="small"
                          fullWidth={true}
                          label="Email"
                          required={true}
                        />
                      </Grid>
                      <Grid size={{xs: 12 }}>
                        <FormControlledTextField variant="outlined"
                          name="password"
                          size="small"
                          fullWidth={true}
                          label="Password"
                          type="password"
                          required={true}
                        />
                      </Grid>
                    </>
                  )}
                  {isResetMode && (
                    <>
                      <Grid size={{xs: 12 }}>
                        <FormControlledTextField variant="outlined"
                          name="password"
                          size="small"
                          fullWidth={true}
                          label="New Password"
                          type="password"
                          required={true}
                        />
                      </Grid>
                      <Grid size={{xs: 12 }}>
                        <FormControlledTextField variant="outlined"
                          name="confirmPassword"
                          size="small"
                          fullWidth={true}
                          label="Confirm New Password"
                          type="password"
                          required={true}
                        />
                      </Grid>
                    </>
                  )}
                  {isRegisterMode && !isResetMode && (
                    <Grid size={{xs: 12 }}>
                      <FormControlledTextField variant="outlined"
                        name="confirmPassword"
                        size="small"
                        fullWidth={true}
                        label="Confirm Password"
                        type="password"
                        required={true}
                      />
                    </Grid>
                  )}
                  {isRegisterMode && !isResetMode && (
                    <>
                      <Grid size={{xs: 12}}>
                        <FormControlledTextField variant="outlined"
                          name="dateOfBirth"
                          size="small"
                          fullWidth={true}
                          label="Date of Birth"
                          type="date"
                          required={true}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Grid>
                      <Grid size={{xs: 12}}>
                        <FormControlledTextField variant="outlined"
                          name="phoneNumber"
                          size="small"
                          fullWidth={true}
                          label="Phone Number"
                          required={true}
                          placeholder="+1-XXX-XXX-XXXX"
                          format={(value?: string) => formatPhoneNumber(value as string | undefined)}
                          parse={(value?: string) => normalizePhoneNumber(value)}
                          onChange={(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                            form.change('phoneNumber', normalizePhoneNumber(event.target.value));
                          }}
                          onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                            if (shouldPreventPhonePrefixDeletion(event)) {
                              event.preventDefault();
                            }
                          }}
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      </Grid>
                    </>
                  )}
                  {!isRegisterMode && !isResetMode && (
                    <Grid size={{ xs: 12 }} sx={{ mt: -1.5 }}>
                      <Button
                        variant="text"
                        type="button"
                        startIcon={<InfoOutlinedIcon />}
                        sx={{ textTransform: 'none', px: 0 }}
                        onClick={() => {
                          const emailValue = form.getState().values?.email as string | undefined;
                          handleForgotPassword(emailValue);
                        }}
                        disabled={isAuthLoading}
                      >
                        Forgot password
                      </Button>
                    </Grid>
                  )}
                  {isRegisterMode && !isResetMode && (
                    <Grid size={{ xs: 12 }} sx={{ mt: -1.5 }}>
                      <Button
                        variant="text"
                        type="button"
                        startIcon={<InfoOutlinedIcon />}
                        sx={{ textTransform: 'none', px: 0 }}
                        onClick={() => {
                          const emailValue = form.getState().values?.email as string | undefined;
                          handleResendVerification(emailValue);
                        }}
                        disabled={isAuthLoading}
                      >
                        Resend verification email
                      </Button>
                    </Grid>
                  )}
                  <Grid size={{xs: 12 }} sx={{justifyItems: 'flex-end'}}>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', alignItems: 'center' }}>
                      {!isResetMode && (
                        <StyledButton
                          variant="outlined"
                          type="button"
                          onClick={() => {
                            setIsRegisterMode((prev) => !prev)
                            setHasRequestedPasswordReset(false)
                          }}
                        >
                          {isRegisterMode ? 'Back to login' : 'Sign up'}
                        </StyledButton>
                      )}
                      {isResetMode && (
                        <StyledButton
                          variant="outlined"
                          type="button"
                          onClick={() => navigate('/login', { replace: true })}
                        >
                          Back to login
                        </StyledButton>
                      )}
                      {
                        isAuthLoading
                          ?
                        <CircularProgress/>
                          :
                        <StyledButton
                          variant="contained"
                          fullWidth={false}
                          type="submit"
                        >
                          {isResetMode ? 'Change password' : (isRegisterMode ? 'Create account' : 'Login')}
                        </StyledButton>
                      }
                    </Box>
                  </Grid>
                </Grid>
              </form>
            )}
          />
          )}
        </Paper>
        {!isRegisterMode && !isResetMode && hasRegisteredSuccessfully && (
          <Box
            sx={{
              width: '100%',
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(96, 165, 250, 0.2)',
              border: '1px solid rgba(96, 165, 250, 0.5)',
              color: 'primary.dark',
              fontSize: '0.9rem',
              lineHeight: 1.4,
            }}
          >
            Registration successful. Please check your email and verify your account to complete signup.
          </Box>
        )}
        {!isRegisterMode && !isResetMode && hasRequestedPasswordReset && (
          <Box
            sx={{
              width: '100%',
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 1.5,
              backgroundColor: 'rgba(96, 165, 250, 0.2)',
              border: '1px solid rgba(96, 165, 250, 0.5)',
              color: 'primary.dark',
              fontSize: '0.9rem',
              lineHeight: 1.4,
            }}
          >
            Password reset email sent successfully, reset link expires in 1 hour.
          </Box>
        )}
      </Box>
    </Box>
  )
}